import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { configuration } from "../configuration/ConfigurationService";
import { InteractionStatus } from "@azure/msal-browser";

import { PubSubEvents } from "../PubSubEvents";
import { appSessionStorage } from "../storage/SessionStorageService";
import { VIEWER_TOKEN_KEY } from "./AuthService";

export interface Authentication {
  logout: () => void;
  getAccessToken: () => Promise<string>;
  loginAndObtainToken: (
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => void;
}

export class AuthenticationService implements Authentication {
  private msalInstance: IPublicClientApplication | undefined;
  viewerAuthToken: string | null | undefined;
  userAccount: AccountInfo | null | undefined;
  loginInProgress: InteractionStatus | undefined;
  private resolveCallback: Array<(token: string) => void> = [];
  private needsSetup: boolean = true;

  loginAndObtainToken = async (
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) => {
    this.setAuthProperties(msalInstance, userAccount, viewerAuthToken, loginInProgress);

    const accessToken = await this.getAccessToken();
    this.sendAccessToken(accessToken);
  };

  private setAuthProperties(
    msalInstance: IPublicClientApplication,
    userAccount: AccountInfo | null,
    viewerAuthToken: string | null,
    loginInProgress: InteractionStatus
  ) {
    this.msalInstance = msalInstance;
    this.userAccount = userAccount;
    this.viewerAuthToken = viewerAuthToken;
    this.loginInProgress = loginInProgress;
    this.needsSetup = false;
  }

  getAccessToken = async () => {
    if (this.needsSetup) {
      return new Promise<string>((resolve, _) => {
        this.resolveCallback.push(resolve);
      });
    } else {
      if (this.viewerAuthToken) {
        appSessionStorage.setStorageItem(VIEWER_TOKEN_KEY, this.viewerAuthToken as string);
        return this.viewerAuthToken;
      } else {
        this.login();
        const token = await this.getAppAuthenticationToken();
        return token;
      }
    }
  };

  private login = async () => {
    if (
      !this.viewerAuthToken &&
      !this.userAccount &&
      this.loginInProgress === InteractionStatus.None
    ) {
      await this.msalInstance?.loginRedirect();
    }
  };

  private getAppAuthenticationToken = async (): Promise<string> => {
    let config = await configuration.get();
    if (this.userAccount && config.scopes && this.msalInstance) {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: config.scopes,
        account: this.userAccount,
      });
      return response.accessToken;
    }
    return "";
  };

  private sendAccessToken = (token: string) => {
    if (this.resolveCallback && token) {
      this.resolveCallback.forEach((resolve) => resolve(token));
    }
  };

  logout = () => {
    PubSub.subscribe(PubSubEvents.AllLockedInspectionsReleased, () => {
      appSessionStorage.clear();
      if (this.msalInstance) this.msalInstance.logout();
    });
    PubSub.publish(PubSubEvents.Logout);
  };
}
export const authentication = new AuthenticationService();
